type TQuery = Record<string, string | string[] | undefined>;

/**
 * Repairs a malformed URL where one or more stray `?` nested extra params
 * inside a value. e.g. `?tab=checkout?repAccountId=18391375` — the browser
 * parses `tab` as `"checkout?repAccountId=18391375"` and drops `repAccountId`
 * entirely. This splits the value on every `?`, keeps the first part as the
 * clean value, and recovers the trailing params (without overwriting any param
 * already present — existing values win).
 *
 * Splitting on all `?` and rejoining the remainder with `&` means a `?` used
 * repeatedly in place of `&` (e.g. `repAccountId=1?repName=A?tab=checkout`) is
 * recovered fully, not just the first segment.
 *
 * This matters for rep links specifically: nothing generates them, so they are
 * typed by hand, which is exactly where `?` gets used in place of `&`. Without
 * this, such a link silently attributes nothing.
 */
export function repairNestedQuery(query: TQuery): TQuery {
  const repaired: TQuery = { ...query };

  for (const [key, value] of Object.entries(query)) {
    if (typeof value !== "string" || !value.includes("?")) continue;
    const [clean, ...rest] = value.split("?");
    repaired[key] = clean;
    new URLSearchParams(rest.join("&")).forEach((val, recoveredKey) => {
      if (repaired[recoveredKey] === undefined) repaired[recoveredKey] = val;
    });
  }

  return repaired;
}
